/**
 * Onboarding Dialog for First-Time Cache Download
 * Detects network quality and offers appropriate download options
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  LinearProgress,
  Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { cacheService, PRIORITY_COUNTIES } from '../services/cache';
import { useTranslation } from '../i18n/translations';

interface OnboardingDialogProps {
  open: boolean;
  onComplete: (downloadStarted: boolean) => void;
}

export function OnboardingDialog({ open, onComplete }: OnboardingDialogProps) {
  const { t } = useTranslation();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState('');
  const [estimatedTime, setEstimatedTime] = useState('');
  const [useSnapshots, setUseSnapshots] = useState(() => {
    const saved = localStorage.getItem('preferSnapshots');
    return saved === null ? false : saved === 'true'; // Default to false until R2 snapshots are set up
  });

  const snapshotHost = (import.meta as any).env?.VITE_SNAPSHOT_BASE_URL || 'https://snapshots.ziawellnav.pages.dev';
  const [connectionQuality, setConnectionQuality] = useState<{ type: string; downlink: number; fast: boolean }>({
    type: 'unknown',
    downlink: 0,
    fast: true
  });

  useEffect(() => {
    if (open) {
      const quality = cacheService.getConnectionQuality();
      setConnectionQuality(quality);
    }
  }, [open]);

  const handleDownload = async (priorityOnly: boolean) => {
    setDownloading(true);
    
    try {
      const totalTasks = priorityOnly ? 
        (PRIORITY_COUNTIES.length * 4) : // 4 facility types per county
        (cacheService.getAllCountyCount() * 4); // dynamic total for all NM counties
      
      let completedTasks = 0;
      console.log(`📥 Starting download: ${priorityOnly ? 'Priority' : 'All'} counties (${totalTasks} total tasks)`);

      await cacheService.cacheAllFacilities((progressInfo) => {
        if (progressInfo.taskComplete) {
          completedTasks++;
        }

        const batchLabel = progressInfo.currentBatch
          ? ` (batch ${progressInfo.currentBatch}${progressInfo.totalBatches ? `/${progressInfo.totalBatches}` : ''})`
          : '';
        const percentComplete = progressInfo.percentComplete !== undefined
          ? progressInfo.percentComplete
          : (completedTasks / totalTasks) * 100;

        setProgress(Math.min(100, percentComplete));
        
        const taskLabel = `${progressInfo.county} - ${progressInfo.facilityType}${batchLabel}`;
        setCurrentTask(taskLabel);
        console.log(`✅ Progress ${percentComplete.toFixed(1)}%: ${taskLabel}`);
        
        if (progressInfo.estimatedSecondsRemaining > 0) {
          const minutes = Math.floor(progressInfo.estimatedSecondsRemaining / 60);
          const seconds = Math.floor(progressInfo.estimatedSecondsRemaining % 60);
          const timeLabel = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
          setEstimatedTime(timeLabel);
          console.log(`   ⏱️  Estimated time remaining: ${timeLabel}`);
        }
      }, priorityOnly, false, useSnapshots && !priorityOnly);

      console.log(`🎉 Download complete! ${completedTasks} tasks finished.`);
      
      if (dontShowAgain) {
        localStorage.setItem('hasSeenOnboarding', 'true');
      }

      onComplete(true);
    } catch (error) {
      console.error('❌ Download failed:', error);
      alert('Failed to download data. Please check your connection and try again.');
      setDownloading(false);
    }
  };

  const handleSkip = () => {
    if (dontShowAgain) {
      localStorage.setItem('hasSeenOnboarding', 'true');
    }
    onComplete(false);
  };

  const getEstimatedSize = (priorityOnly: boolean) => {
    // These are conservative: full datasets are much larger than the old 1-3 MB estimate
    return priorityOnly ? 'tens of MB (Eddy + Lea)' : 'hundreds of MB (all counties)';
  };

  const getEstimatedDuration = (priorityOnly: boolean) => {
    const { downlink, fast } = connectionQuality;
    
    if (!fast) {
      return priorityOnly ? '5-15 min' : '20-40 min+';
    }
    
    if (downlink >= 10) {
      return priorityOnly ? '2-5 min' : '10-20 min';
    } else if (downlink >= 5) {
      return priorityOnly ? '5-10 min' : '20-30 min';
    } else if (downlink >= 2) {
      return priorityOnly ? '10-20 min' : '30-60 min';
    }
    
    return priorityOnly ? '10-20 min' : '30-60 min';
  };

  return (
    <Dialog
      open={open}
      onClose={downloading ? undefined : handleSkip}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
          border: '1px solid rgba(96, 165, 250, 0.2)',
          borderRadius: '20px',
          m: 2,
          maxHeight: 'calc(100vh - 32px)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 2.5, px: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(96, 165, 250, 0.3)'
          }}>
            <DownloadIcon sx={{ fontSize: 24, color: '#60a5fa' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
              {t('onboardingTitle')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              {t('onboardingSubtitle')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 1.5 }}>
        {!downloading ? (
          <>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, lineHeight: 1.5 }}>
              {t('onboardingDescription')}
            </Typography>

            {/* Priority Counties Card */}
            <Box 
              onClick={() => handleDownload(true)}
              sx={{ 
                p: 2,
                mb: 1.5,
                backgroundColor: '#0f172a',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '14px',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(96, 165, 250, 0.25)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                transform: 'scale(1)',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 14px 32px rgba(96, 165, 250, 0.4)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#60a5fa', fontWeight: 700 }}>
                    {t('onboardingPriority')}
                  </Typography>
                  <Chip 
                    label={t('onboardingRecommended')} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.65rem', 
                      backgroundColor: 'rgba(96, 165, 250, 0.25)', 
                      color: '#60a5fa',
                      fontWeight: 600
                    }} 
                  />
                </Box>
                <DownloadIcon sx={{ fontSize: 20, color: '#60a5fa' }} />
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 1 }}>
                {PRIORITY_COUNTIES.join(' • ')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  📦 {getEstimatedSize(true)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  ⏱️ {getEstimatedDuration(true)}
                </Typography>
              </Box>
            </Box>

            {/* All Counties Card */}
            <Box 
              onClick={() => handleDownload(false)}
              sx={{ 
                p: 2,
                mb: 2,
                backgroundColor: '#0f172a',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '14px',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(139, 92, 246, 0.25)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                transform: 'scale(1)',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 14px 32px rgba(139, 92, 246, 0.4)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#a78bfa', fontWeight: 700 }}>
                  {t('onboardingAllCounties')}
                </Typography>
                <DownloadIcon sx={{ fontSize: 20, color: '#a78bfa' }} />
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 1 }}>
                {t('onboardingCompleteCoverage')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  📦 {getEstimatedSize(false)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  ⏱️ {getEstimatedDuration(false)}
                </Typography>
              </Box>
            </Box>

            {/* CDN Toggle - Compact */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '10px',
              mb: 1.5
            }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600, display: 'block' }}>
                  {t('onboardingFastCDN')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem' }}>
                  {useSnapshots ? snapshotHost : 'Uses live API'}
                </Typography>
              </Box>
              <Checkbox
                checked={useSnapshots}
                onChange={(e) => {
                  setUseSnapshots(e.target.checked);
                  localStorage.setItem('preferSnapshots', String(e.target.checked));
                }}
                size="small"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.3)',
                  '&.Mui-checked': { color: '#60a5fa' }
                }}
              />
            </Box>

            {/* Don't show again */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              px: 0.5
            }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                {t('onboardingDontShowAgain')}
              </Typography>
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                size="small"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.3)',
                  '&.Mui-checked': { color: '#60a5fa' }
                }}
              />
            </Box>
          </>
        ) : (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2 
            }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600 }}>
                {t('onboardingDownloading')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#60a5fa', fontWeight: 700 }}>
                {progress.toFixed(0)}%
              </Typography>
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{
                mb: 2,
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #60a5fa 0%, #a78bfa 100%)',
                  borderRadius: 3
                }
              }}
            />

            <Box sx={{ 
              p: 1.5, 
              backgroundColor: 'rgba(255, 255, 255, 0.03)', 
              borderRadius: '10px',
              mb: 2
            }}>
              <Typography variant="caption" sx={{ 
                color: 'rgba(255, 255, 255, 0.6)', 
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {currentTask}
              </Typography>
              {estimatedTime && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', mt: 0.5, display: 'block' }}>
                  ~{estimatedTime} remaining
                </Typography>
              )}
            </Box>

            <Typography variant="caption" sx={{ 
              color: 'rgba(255, 255, 255, 0.5)', 
              display: 'block',
              textAlign: 'center',
              lineHeight: 1.4
            }}>
              {t('onboardingBackgroundNote')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
        {!downloading ? (
          <Button 
            onClick={handleSkip}
            fullWidth
            sx={{ 
              color: 'rgba(255, 255, 255, 0.5)',
              py: 1,
              fontSize: '0.85rem',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            {t('onboardingSkipForNow')}
          </Button>
        ) : (
          <Button 
            onClick={() => onComplete(true)}
            fullWidth
            variant="contained"
            sx={{ 
              py: 1.5,
              borderRadius: '12px',
              background: 'linear-gradient(90deg, #60a5fa 0%, #a78bfa 100%)',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)'
              }
            }}
          >
            {t('onboardingContinueUsing')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
